import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDateRangeStore } from '@/stores/dateRangeStore';
import type { DatePreset } from '@/types';

export function DateRangeSelector() {
  const { t, i18n } = useTranslation();
  const { preset, startDate, endDate, setPreset, setCustomRange } = useDateRangeStore();
  const [showCustom, setShowCustom] = useState(false);
  const [showMonthPicker, setShowMonthPicker] = useState(false);

  const presets: { key: DatePreset; label: string }[] = [
    { key: 'today', label: t('dateRange.today') },
    { key: 'this_week', label: t('dateRange.thisWeek') },
    { key: 'this_month', label: t('dateRange.thisMonth') },
    { key: 'custom', label: t('dateRange.custom') },
  ];

  const handlePresetClick = (key: DatePreset) => {
    if (key === 'custom') {
      setShowCustom(true);
      setShowMonthPicker(false);
    } else {
      setShowCustom(false);
      setShowMonthPicker(false);
      setPreset(key);
    }
  };

  const handleCustomDateChange = (type: 'start' | 'end', value: string) => {
    const date = new Date(value);
    if (type === 'start') {
      setCustomRange(date, endDate);
    } else {
      setCustomRange(startDate, date);
    }
  };

  const handleMonthSelect = (year: number, month: number) => {
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0, 23, 59, 59);
    setCustomRange(start, end);
    setShowMonthPicker(false);
  };

  // Generate last 12 months for picker
  const getLastMonths = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        year: date.getFullYear(),
        month: date.getMonth(),
        label: date.toLocaleDateString('he-IL', { month: 'long', year: 'numeric' }),
      });
    }
    return months;
  };

  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <div className="flex flex-wrap items-center gap-2">
        {/* Preset buttons */}
        {presets.map((p) => (
          <button
            key={p.key}
            onClick={() => handlePresetClick(p.key)}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              preset === p.key && p.key !== 'custom'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            )}
          >
            {p.label}
          </button>
        ))}

        {/* Month picker button */}
        <button
          onClick={() => {
            setShowMonthPicker(!showMonthPicker);
            setShowCustom(false);
          }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
        >
          <Calendar className="w-4 h-4" />
          {t('dateRange.otherMonths')}
          <ChevronDown className={cn('w-4 h-4 transition-transform', showMonthPicker && 'rotate-180')} />
        </button>
      </div>

      {/* Month picker dropdown */}
      {showMonthPicker && (
        <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-2">
          {getLastMonths().map((m) => (
            <button
              key={`${m.year}-${m.month}`}
              onClick={() => handleMonthSelect(m.year, m.month)}
              className="px-3 py-2 text-sm rounded-lg bg-gray-50 hover:bg-blue-50 hover:text-blue-600 transition-colors"
            >
              {m.label}
            </button>
          ))}
        </div>
      )}

      {/* Custom date range inputs */}
      {showCustom && (
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">{t('dateRange.from')}</label>
            <input
              type="date"
              value={formatDate(startDate)}
              onChange={(e) => handleCustomDateChange('start', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">{t('dateRange.to')}</label>
            <input
              type="date"
              value={formatDate(endDate)}
              onChange={(e) => handleCustomDateChange('end', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      )}

      {/* Current range display */}
      <div className="mt-3 text-sm text-gray-500">
        {startDate.toLocaleDateString(i18n.language === 'he' ? 'he-IL' : 'en-US')} - {endDate.toLocaleDateString(i18n.language === 'he' ? 'he-IL' : 'en-US')}
      </div>
    </div>
  );
}
